<script setup lang="ts">
import { computed } from "vue";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "@/components/ui/AppSidebar.vue";
import { usePreferencesStore } from "@/stores/preferences";

// Sidebar width + collapsed state live in the persisted preferences store, so new tabs
// open with the user's last choice. Before the first change, `open` falls back to the
// legacy `sidebar_state` cookie the ShadCN provider used to write.
const preferences = usePreferencesStore();
const sidebarOpen = computed({
  get: () => preferences.sidebarOpen ?? !document.cookie.includes("sidebar_state=false"),
  set: (value: boolean) => preferences.setSidebarOpen(value),
});
const sidebarWidth = computed({
  get: () => preferences.sidebarWidth ?? undefined,
  set: (value: number | undefined) => {
    if (value !== undefined) preferences.setSidebarWidth(value);
  },
});

defineProps<{
  paddedContent?: boolean;
  /** While something lies over the main content (the `overlay` slot): it can't be reached, the header and sidebar can */
  contentInert?: boolean;
}>()
</script>

<template>
  <SidebarProvider v-model:open="sidebarOpen" v-model:width="sidebarWidth">
    <!-- The sidebar -->
    <AppSidebar />
    <!--
      The inset is exactly the viewport height and does not scroll itself: the app
      bar keeps its place and the page area below it takes the remaining height and
      scrolls internally. That is what lets `PageView` keep a page header pinned
      while its content scrolls under it.
    -->
    <SidebarInset class="h-dvh overflow-hidden">
      <!-- Header of the content -->
      <header class="flex shrink-0 flex-col">
        <slot name="header" />
      </header>

      <!-- Main content, with room for what lies over it but not over the header -->
      <div class="relative flex min-h-0 flex-1 flex-col">
        <div
          class="flex min-h-0 flex-1 flex-col overflow-y-auto"
          :class="{ 'px-2': paddedContent }"
          :inert="contentInert"
        >
          <slot />
        </div>
        <slot name="overlay" />
      </div>
    </SidebarInset>
  </SidebarProvider>
</template>
